import { useEffect, useCallback } from "react";
import { useGlobalSearchStore } from "../stores/GlobalSearchStore";
import { requestLibrarySearchFocus } from "src/features/library/search/librarySearchFocus";

/**
 * Detects if user is on macOS
 */
const isMac = (): boolean => {
  return navigator.platform.toUpperCase().indexOf("MAC") >= 0;
};

/**
 * Hook to handle global keyboard shortcuts for search
 * Ctrl/Cmd + K focuses the Library pane's search box when a workspace is
 * mounted (#2377); otherwise it opens/focuses the search modal
 * Escape closes search modal
 */
export const useGlobalSearchKeyboard = () => {
  const { isOpen, toggleSearch, closeSearch } = useGlobalSearchStore();

  const handleKeyDown = useCallback(
    (event: KeyboardEvent) => {
      // Handle Ctrl/Cmd + K
      const isModifierKey = isMac() ? event.metaKey : event.ctrlKey;

      if (isModifierKey && event.key.toLowerCase() === "k") {
        // Prevent default browser behavior (e.g., Chrome's address bar focus)
        event.preventDefault();
        event.stopPropagation();

        // On the workspace, search lives in the Library pane's box; the
        // modal stays closed (and is retired there).
        if (!isOpen && requestLibrarySearchFocus()) {
          return;
        }

        // If search is open, don't toggle - let internal navigation handle it
        // If closed, open it
        if (!isOpen) {
          toggleSearch();
        } else {
          // If already open, focus the input
          const searchInput = document.querySelector(
            '[data-testid="global-search-input"]',
          ) as HTMLInputElement;
          searchInput?.focus();
        }
        return;
      }

      // Handle Escape - only when search is open
      if (event.key === "Escape" && isOpen) {
        event.preventDefault();
        closeSearch();
        return;
      }
    },
    [isOpen, toggleSearch, closeSearch],
  );

  useEffect(() => {
    // Add listener with capture to get it before other handlers
    document.addEventListener("keydown", handleKeyDown, { capture: true });

    return () => {
      document.removeEventListener("keydown", handleKeyDown, { capture: true });
    };
  }, [handleKeyDown]);
};

/**
 * Hook for keyboard navigation within search results
 */
export const useSearchResultsNavigation = (
  resultsCount: number,
  onSelect: (index: number) => void,
) => {
  const { selectedIndex, setSelectedIndex } = useGlobalSearchStore();

  const handleKeyDown = useCallback(
    (event: KeyboardEvent) => {
      if (resultsCount === 0) return;

      switch (event.key) {
        case "ArrowDown":
          event.preventDefault();
          // From no selection (-1), go to first item (0)
          // Otherwise, go to next item (capped at last)
          if (selectedIndex === -1) {
            setSelectedIndex(0);
          } else {
            setSelectedIndex(Math.min(selectedIndex + 1, resultsCount - 1));
          }
          break;
        case "ArrowUp":
          event.preventDefault();
          // From no selection or first item, stay at first item
          // Otherwise, go to previous item
          if (selectedIndex <= 0) {
            setSelectedIndex(0);
          } else {
            setSelectedIndex(selectedIndex - 1);
          }
          break;
        case "Enter":
          event.preventDefault();
          // Only navigate if something is selected (not -1)
          if (selectedIndex >= 0) {
            onSelect(selectedIndex);
          }
          break;
        case "Home":
          event.preventDefault();
          setSelectedIndex(0);
          break;
        case "End":
          event.preventDefault();
          setSelectedIndex(resultsCount - 1);
          break;
      }
    },
    [resultsCount, selectedIndex, setSelectedIndex, onSelect],
  );

  return { selectedIndex, handleKeyDown };
};
